/** Shared admin Cloud Function proxy (avoids duplicating fetch boilerplate). */
import { auth } from '../../firebase';

/** Map common admin invite failures to actionable messages. */
export function formatAdminInviteError(err: unknown, status?: number): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (status === 429 || /rate limit exceeded/i.test(msg)) {
    return 'Rate limit exceeded — wait a minute and retry.';
  }
  if (/generate sign-in link|sign-in link/i.test(msg)) {
    return `${msg} Check Firebase authorized domains include your host (e.g. localhost).`;
  }
  if (/Application Default Credentials|Google Cloud credentials/i.test(msg)) {
    return msg;
  }
  return msg;
}

export async function callAdminFunction<T = unknown>(
  name: string,
  data: unknown = {},
): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated — sign in first');
  const token = await user.getIdToken();
  const res = await fetch(`/api/functions/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const base = json?.error?.message || json?.error || json?.message || `Function ${name} failed (${res.status})`;
    const detail = typeof json?.detail === 'string' ? ` ${json.detail}` : '';
    const setup = typeof json?.setupCommand === 'string' ? ` Run \`${json.setupCommand}\`.` : '';
    const diagnostic = typeof json?.diagnosticCode === 'string' ? ` (${json.diagnosticCode})` : '';
    const raw = typeof base === 'string' ? `${base}${detail}${setup}${diagnostic}` : JSON.stringify(base);
    throw new Error(formatAdminInviteError(raw, res.status));
  }
  return (json?.result ?? json) as T;
}
